import type { DeepReadonly } from 'vue'
import { select, selectAll } from '@/lib/bulk-selector'
import type { Layer } from './useThreeDLayerGroups'
import type { TextLabel } from '~/lib/three-d-resources/labels-manager'
import type { DrillLabelLayer, DrillCSVFile, DrillLayer } from '~/entities/drillLabel'
import type { DrillDataSetLayerGroup } from '~/composables/useDrillDataSetGroups'

export type ThreeDVisibleElement = Layer | TextLabel | (DrillLabelLayer | DrillLayer)
export type ThreeDElement = Layer | TextLabel | DrillLabelLayer | Category | DrillDataSetLayerGroup | DrillCSVFile | DrillLayer
export type Category = { categoryName: string; layers: Layer[]; type: 'layer' | 'drill'; dataSetId?: number; localizationId?: number }

export interface UseLayerSelect<T> {
  clearLayerSelection(): void
  selectedLayers: DeepReadonly<Ref<T[]>>
  setFocused(isFocused: boolean): void
  selectLayer(item?: DeepReadonly<T>): void
  handleLayerClick(event: Event, item: DeepReadonly<T>): void
}

export function useLayerSelect<T = ThreeDVisibleElement>({
  layers,
  getSelectableId,
  focusLayer = () => {},
  focusLabel = () => {},
  onSelectAll = () => {},
  onDeselectAll = () => {},
  onLayerSelected = () => {},
}: {
  layers: DeepReadonly<Ref<T[]>>
  getSelectableId: (v: DeepReadonly<T>) => string
  focusLayer?: (id: number) => void
  focusLabel?: (id: string) => void
  onSelectAll?: () => void
  onDeselectAll?: () => void
  onLayerSelected?: (item: DeepReadonly<T>) => void
}): UseLayerSelect<T> {
  const idIndices = computed(() => layers.value.map(getSelectableId))
  let selectedIds = ref<string[]>([])
  let currentLayer: DeepReadonly<T> | undefined = layers.value[0]

  const { setFocused } = useKeyboardShortcut({
    opts: {
      a: () => {
        if (selectedIds.value.length == layers.value.length) return
        if (layers.value.length === 0) return
        _selectAllLayers(getSelectableId(layers.value[0]), getSelectableId(layers.value[layers.value.length - 1]))
        onSelectAll()
      },

      d: () => {
        if (selectedIds.value.length <= 1) return
        clearLayerSelection()
        onDeselectAll()
      },

      f: () => {
        if (selectedIds.value.length !== 1) return

        const idParts = selectedIds.value[0].split('-')

        if (idParts.length === 1) {
          focusLayer(parseInt(idParts[0], 10))
        } else {
          focusLabel(idParts[1])
        }
      },
    },
    onFocusOnly: true,
  })

  function handleLayerClick(event: PointerEvent, item: DeepReadonly<T>) {
    if (event.metaKey || event.ctrlKey) {
      onLayerCtrlClick(item)
    } else if (event.shiftKey) {
      onLayerShiftClick(item)
    } else {
      selectLayer(item)
      onLayerSelected(item)
    }
  }

  function onLayerCtrlClick(selected: DeepReadonly<T>) {
    const [nextSelectedId, nextSelectedIds] = select(
      selectedIds.value,
      currentLayer ? getSelectableId(currentLayer) : '',
      getSelectableId(selected),
    )

    selectedIds.value = nextSelectedIds
    currentLayer = layers.value.find(l => getSelectableId(l) === nextSelectedId)
  }

  function onLayerShiftClick(selected: DeepReadonly<T>) {
    _selectAllLayers(currentLayer ? getSelectableId(currentLayer) : '', getSelectableId(selected))
  }

  function selectLayer(layer: DeepReadonly<T>) {
    currentLayer = layer
    if (layer) {
      selectedIds.value = [getSelectableId(layer)]
    } else {
      selectedIds.value = []
    }
  }

  function clearLayerSelection() {
    selectedIds.value = []
  }

  function _selectAllLayers(selectedId: string, targetId: string) {
    const nextSelectedIds = selectAll({
      selectedIds: selectedIds.value,
      selectedId: selectedId,
      ids: idIndices.value,
      id: targetId,
    })

    selectedIds.value = nextSelectedIds
  }

  const selectedLayers = computed(() => {
    const ids = new Set(selectedIds.value)
    return layers.value.filter(layer => ids.has(getSelectableId(layer)))
  })

  return {
    selectedLayers,
    setFocused,
    selectLayer,
    handleLayerClick,
    clearLayerSelection,
  }
}
